import { isWordChar } from './boundaries'
import { parseTaskTitle } from './parseTaskTitle'
import { diffBounds } from './remapRanges'
import type { ParseTitleOptions, TextRange } from './types'

/**
 * **Alles, was in einem Text erkennbar ist** — auch der zweite Treffer derselben Art, den die Regel „je
 * Feld der erste“ sonst verdrängt. Beim Bearbeiten (Entscheidung 4) muss genau das geschützt sein: Sonst
 * rückt „Freitag“ in „von Montag auf Freitag“ nach, sobald „Montag“ als Text gilt (Code-Review 03.10.2026).
 */
export function recognizableRanges(text: string, options: Omit<ParseTitleOptions, 'dismissed'>): TextRange[] {
  const found: TextRange[] = []
  // Jede Runde nimmt die Treffer der vorigen als Text — dann kommt der jeweils nächste derselben Art dran.
  for (;;) {
    const { tokens } = parseTaskTitle(text, { ...options, dismissed: found })
    if (tokens.length === 0) return found.sort((a, b) => a.start - b.start)
    found.push(...tokens.map(({ start, end }) => ({ start, end })))
  }
}

/** Ein unverändertes Stück des gespeicherten Titels: `from` dort, `start`/`end` im aktuellen Text. */
export interface KeptSegment {
  from: number
  start: number
  end: number
}

/**
 * **Welche Stücke des gespeicherten Titels noch unberührt dastehen**, Schritt für Schritt mitgeführt.
 * Ein einziger Vergleich vom gespeicherten zum aktuellen Titel kennt nur EINE geänderte Strecke — wer vorn
 * und hinten tippt, ließe alles dazwischen als geändert erscheinen.
 */
export function remapSegments(prevText: string, nextText: string, segments: KeptSegment[]): KeptSegment[] {
  if (prevText === nextText) return segments
  const { prefix, changedEnd, delta } = diffBounds(prevText, nextText)
  return segments.flatMap((seg) => {
    const out: KeptSegment[] = []
    if (seg.start < prefix) out.push({ from: seg.from, start: seg.start, end: Math.min(seg.end, prefix) })
    if (seg.end > changedEnd) {
      const start = Math.max(seg.start, changedEnd)
      out.push({ from: seg.from + (start - seg.start), start: start + delta, end: seg.end + delta })
    }
    return out
  })
}

/**
 * Stellen des gespeicherten Titels (`ranges`, dort gezählt) an ihrem Platz im aktuellen Text — nur die,
 * deren Wort ganz in einem unberührten Stück liegt. Direkt daran Getipptes macht ein neues Wort daraus
 * („morgens“), das darf erkannt werden — wie bei `remapRanges`.
 */
export function carryRanges(original: string, current: string, segments: KeptSegment[], ranges: TextRange[]): TextRange[] {
  return ranges.flatMap((range) => {
    const seg = segments.find(s => s.from <= range.start && range.end <= s.from + (s.end - s.start))
    if (!seg) return []
    const start = seg.start + (range.start - seg.from)
    const end = start + (range.end - range.start)
    if (isWordChar(current[start - 1]) && !isWordChar(original[range.start - 1])) return []
    if (isWordChar(current[end]) && !isWordChar(original[range.end])) return []
    return [{ start, end }]
  })
}
